/**
 * PerfDashboard Component
 * Real-time performance monitoring dashboard with alerts panel
 */

import { useState } from 'react';
import {
  Activity,
  Wifi,
  WifiOff,
  Database,
  Server,
  Zap,
  Clock,
  AlertTriangle,
  AlertCircle,
  Bell,
  BellOff,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Check,
  Radio,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, Button, Badge } from '@makanly/ui-kit';
import { PerfCard } from './PerfCard';
import { usePerfMonitor } from '../hooks/usePerfMonitor';
import { PerfAlert, PROBE_CONFIG, LATENCY_THRESHOLDS } from '../types/perf.types';

interface PerfDashboardProps {
  compact?: boolean;
}

export function PerfDashboard({ compact = false }: PerfDashboardProps) {
  const [showAlerts, setShowAlerts] = useState(true);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);

  const { state, probeNow, acknowledgeAlert, clearAlerts } = usePerfMonitor({
    autoStart: true,
    enableBrowserNotifications: notificationsEnabled,
    onAlert: (alert) => {
      console.log('[PerfDashboard] Alert received:', alert);
    },
  });

  const { metrics, isConnected, isProbing, lastProbeTime, lastError, alerts } = state;

  // Request notification permission
  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      setNotificationsEnabled(permission === 'granted');
    }
  };

  // Format last probe time
  const lastProbeFormatted = lastProbeTime ? new Date(lastProbeTime).toLocaleTimeString() : 'Never';

  // Count unacknowledged alerts
  const unacknowledgedAlerts = alerts.filter((a) => !a.acknowledged);
  const criticalAlerts = unacknowledgedAlerts.filter((a) => a.level === 'critical');
  const warningAlerts = unacknowledgedAlerts.filter((a) => a.level === 'warning');

  // Determine overall health
  const overallHealth =
    criticalAlerts.length > 0 ? 'critical' : warningAlerts.length > 0 ? 'warning' : 'healthy';

  if (compact) {
    return <CompactPerfDashboard state={state} probeNow={probeNow} />;
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {/* Title and status */}
            <div className="flex items-center gap-3">
              <div
                className={`p-2 rounded-lg ${
                  overallHealth === 'critical'
                    ? 'bg-red-100 dark:bg-red-950/50'
                    : overallHealth === 'warning'
                      ? 'bg-yellow-100 dark:bg-yellow-950/50'
                      : 'bg-green-100 dark:bg-green-950/50'
                }`}
              >
                <Activity
                  className={`w-5 h-5 ${
                    overallHealth === 'critical'
                      ? 'text-red-600 dark:text-red-400'
                      : overallHealth === 'warning'
                        ? 'text-yellow-600 dark:text-yellow-400'
                        : 'text-green-600 dark:text-green-400'
                  }`}
                />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-text-primary">Performance Monitor</h2>
                <div className="flex items-center gap-2 text-sm text-text-secondary">
                  <Clock className="w-3 h-3" />
                  <span>Last probe: {lastProbeFormatted}</span>
                  <span className="text-text-secondary/50">|</span>
                  <span>Interval: {PROBE_CONFIG.INTERVAL_MS / 1000}s</span>
                </div>
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center gap-2">
              {/* Connection status */}
              <div
                className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                  isConnected
                    ? 'bg-green-100 dark:bg-green-950/50 text-green-700 dark:text-green-400'
                    : 'bg-red-100 dark:bg-red-950/50 text-red-700 dark:text-red-400'
                }`}
              >
                {isConnected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
                {isConnected ? 'Connected' : 'Disconnected'}
              </div>

              {/* Notifications toggle */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (!notificationsEnabled) {
                    requestNotificationPermission();
                  } else {
                    setNotificationsEnabled(false);
                  }
                }}
                className="px-2 py-1 h-auto"
              >
                {notificationsEnabled ? (
                  <Bell className="w-4 h-4 text-green-600" />
                ) : (
                  <BellOff className="w-4 h-4 text-gray-400" />
                )}
              </Button>

              {/* Refresh button */}
              <Button
                variant="outline"
                size="sm"
                onClick={probeNow}
                disabled={isProbing}
                className="px-2 py-1 h-auto"
              >
                <RefreshCw className={`w-4 h-4 ${isProbing ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>

          {/* Error message */}
          {lastError && (
            <div className="mt-3 p-2 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-md text-sm text-red-700 dark:text-red-400 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {lastError}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Alerts Panel */}
      {unacknowledgedAlerts.length > 0 && (
        <Card
          className={`border-l-4 ${
            criticalAlerts.length > 0 ? 'border-l-red-500' : 'border-l-yellow-500'
          }`}
        >
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                {criticalAlerts.length > 0 ? (
                  <AlertCircle className="w-4 h-4 text-red-500" />
                ) : (
                  <AlertTriangle className="w-4 h-4 text-yellow-500" />
                )}
                Active Alerts ({unacknowledgedAlerts.length})
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAlerts}
                  className="text-xs px-2 py-1 h-auto"
                >
                  Clear All
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAlerts(!showAlerts)}
                  className="px-2 py-1 h-auto"
                >
                  {showAlerts ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
          </CardHeader>
          {showAlerts && (
            <CardContent className="pt-0">
              <div className="space-y-2">
                {unacknowledgedAlerts.slice(0, 5).map((alert) => (
                  <AlertRow
                    key={alert.id}
                    alert={alert}
                    onAcknowledge={() => acknowledgeAlert(alert.id)}
                  />
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Total Latency Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <PerfCard
            metric={metrics.totalLatency}
            icon={<Zap className="w-5 h-5" />}
            description="End-to-end request latency through all layers"
          />
        </div>
        <Card>
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-text-primary mb-3">Thresholds</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <span className="text-sm text-text-secondary">Excellent</span>
                </div>
                <span className="text-sm font-mono">&lt; {LATENCY_THRESHOLDS.GREEN}ms</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-yellow-500" />
                  <span className="text-sm text-text-secondary">Warning</span>
                </div>
                <span className="text-sm font-mono">
                  {LATENCY_THRESHOLDS.GREEN}-{LATENCY_THRESHOLDS.YELLOW}ms
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <span className="text-sm text-text-secondary">Critical</span>
                </div>
                <span className="text-sm font-mono">&gt; {LATENCY_THRESHOLDS.YELLOW}ms</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Network & Gateway */}
      <section>
        <h3 className="text-sm font-semibold text-text-primary mb-3 flex items-center gap-2">
          <Server className="w-4 h-4" />
          Network & Gateway
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <PerfCard
            metric={metrics.wsRoundTrip}
            icon={<Wifi className="w-4 h-4" />}
            description="WebSocket round-trip time (perf:ping/pong)"
          />
          <PerfCard
            metric={metrics.gateway}
            icon={<Server className="w-4 h-4" />}
            description="API Gateway processing overhead"
          />
        </div>
      </section>

      {/* Database Latencies */}
      <section>
        <h3 className="text-sm font-semibold text-text-primary mb-3 flex items-center gap-2">
          <Database className="w-4 h-4" />
          Database Latencies
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <PerfCard
            metric={metrics.authDb}
            icon={<Database className="w-4 h-4" />}
            description="Auth Service database probe"
          />
          <PerfCard
            metric={metrics.catalogDb}
            icon={<Database className="w-4 h-4" />}
            description="Catalog Service database probe"
          />
          <PerfCard
            metric={metrics.ordersDb}
            icon={<Database className="w-4 h-4" />}
            description="Orders Service database probe"
          />
        </div>
      </section>

      {/* Cache & Messaging */}
      <section>
        <h3 className="text-sm font-semibold text-text-primary mb-3 flex items-center gap-2">
          <Zap className="w-4 h-4" />
          Cache & Messaging
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <PerfCard
            metric={metrics.redis}
            icon={<Zap className="w-4 h-4" />}
            description="Redis cache ping latency"
          />
          <PerfCard
            metric={metrics.nats}
            icon={<Radio className="w-4 h-4" />}
            description="NATS JetStream flush latency"
          />
        </div>
      </section>
    </div>
  );
}

/**
 * Compact version of the dashboard for embedding
 */
function CompactPerfDashboard({
  state,
  probeNow,
}: {
  state: ReturnType<typeof usePerfMonitor>['state'];
  probeNow: () => Promise<void>;
}) {
  const { metrics, isConnected, isProbing, lastProbeTime } = state;

  const lastProbeFormatted = lastProbeTime ? new Date(lastProbeTime).toLocaleTimeString() : '-';

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Activity className="w-4 h-4" />
            Performance
          </CardTitle>
          <div className="flex items-center gap-2">
            <span
              className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={probeNow}
              disabled={isProbing}
              className="px-1 py-0 h-auto"
            >
              <RefreshCw className={`w-3 h-3 ${isProbing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-1">
          {Object.values(metrics).map((metric) => (
            <PerfCard key={metric.name} metric={metric} compact />
          ))}
        </div>
        <div className="mt-2 pt-2 border-t border-border text-xs text-text-secondary text-center">
          Last probe: {lastProbeFormatted}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Individual alert row
 */
function AlertRow({ alert, onAcknowledge }: { alert: PerfAlert; onAcknowledge: () => void }) {
  const timeAgo = getTimeAgo(alert.timestamp);

  return (
    <div
      className={`flex items-center gap-3 p-2 rounded-md ${
        alert.level === 'critical'
          ? 'bg-red-50 dark:bg-red-950/30'
          : 'bg-yellow-50 dark:bg-yellow-950/30'
      }`}
    >
      {alert.level === 'critical' ? (
        <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
      ) : (
        <AlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-text-primary">{alert.displayName}</span>
          <Badge
            variant={alert.level === 'critical' ? 'destructive' : 'warning'}
            className="text-xs"
          >
            {alert.level}
          </Badge>
        </div>
        <p className="text-xs text-text-secondary truncate">{alert.message}</p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className="text-xs text-text-secondary">{timeAgo}</span>
        <Button variant="ghost" size="sm" onClick={onAcknowledge} className="px-1 py-0 h-auto">
          <Check className="w-3 h-3 text-green-600" />
        </Button>
      </div>
    </div>
  );
}

/**
 * Format timestamp as relative time
 */
function getTimeAgo(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);

  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

export default PerfDashboard;
